/* eslint-disable max-lines */
import { useTheme } from '@mui/material'
import React from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  ReferenceDot,
  ReferenceLine,
} from 'recharts'

const formatNumber = (num: number): string => {
  if (!num) {
    return ''
  }

  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K`
  }

  return num.toString()
}

interface ValueRangeChartProps {
  data: any
  price?: number
  lowerPrice?: number
  upperPrice?: number
}

const ValueRangeChart = ({
  data,
  price,
  lowerPrice,
  upperPrice,
}: ValueRangeChartProps) => {
  const theme = useTheme()

  if (data.length === 0) {
    return null
  }

  const minValue = Math.min(
    ...data.map((cat: any) => Math.min(cat.min, cat.max, cat.price)),
  )
  const maxValue = Math.max(
    ...data.map((cat: any) => Math.max(cat.min, cat.max, cat.price)),
  )

  const padding = (maxValue - minValue) * 0.7
  const domainMin = Math.floor(minValue - padding)
  const domainMax = Math.max(Math.ceil(maxValue + padding), upperPrice ?? 0)

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        margin={{
          top: 0,
          right: 40,
          left: -20,
          bottom: 0,
        }}
      >
        <YAxis
          dataKey="y"
          domain={[domainMin, domainMax]}
          type="number"
          axisLine={false}
          tick={false}
          allowDataOverflow
          width={0}
        />

        <XAxis
          dataKey="x"
          domain={[-10, data.length * 20 - 10]}
          type="number"
          axisLine={false}
          tick={false}
          allowDataOverflow
          height={0}
        />

        {price && (
          <ReferenceLine
            y={price}
            stroke={theme?.palette?.primary?.main || '#00ade6'}
            strokeWidth={1}
          />
        )}

        {lowerPrice && (
          <ReferenceLine
            y={lowerPrice}
            stroke={theme?.palette?.primary?.main || '#00ade6'}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}

        {upperPrice && (
          <ReferenceLine
            y={upperPrice}
            stroke={theme?.palette?.primary?.main || '#00ade6'}
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}

        {data.map((data: any, index: any) => (
          <React.Fragment key={data.name}>
            <Line
              strokeWidth={5}
              data={[
                { x: index * 20, y: data.min },
                { x: index * 20, y: data.max },
              ]}
              dot={false}
              type="monotone"
              dataKey="y"
              stroke={data.color}
              tooltipType="none"
            />
            <ReferenceDot
              x={index * 20}
              y={data.min}
              r={0}
              label={{
                position: 'bottom',
                value: formatNumber(data.min),
                fill: 'black',
                offset: 15,
              }}
            />
            <ReferenceDot
              x={index * 20}
              y={data.max}
              r={0}
              label={{
                position: 'top',
                value: formatNumber(data.max),
                fill: 'black',
                offset: 15,
              }}
            />
            <ReferenceDot
              x={index * 20}
              y={data.price}
              r={5}
              fill={data.color}
              label={{
                position: 'right',
                value: formatNumber(data.price),
                fill: 'black',
                offset: 15,
              }}
            />
          </React.Fragment>
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

export default ValueRangeChart
